import { TableRow } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { AssetCell } from "~/components/FirewatchRow/AssetCell";
import { BlastRadiusCell } from "~/components/FirewatchRow/BlastRadiusCell";
import { CheckCell } from "~/components/FirewatchRow/CheckCell";
import { DateCell } from "~/components/FirewatchRow/DateCell";
import { PolicyNameCell } from "~/components/FirewatchRow/PolicyNameCell";
import { RiskValueCell } from "~/components/FirewatchRow/RiskValueCell";
import { VulnerabilityCell } from "~/components/FirewatchRow/VulnerabilityCell";
import { AggregateScoreType, ScoreRating } from "~/operations";
import { AdvisoryCell } from "~/components/FirewatchRow/AdvisoryCell";
import { FirstFoundCell } from "~/components/FirewatchRow/FirstFoundCell";
import { Header } from "~/components/FirewatchPage/FirewatchPageColumns";
import { Space } from "~/lib/types";
import { AdjustedAggregateScoreType } from "~/components/FirewatchPage";
import { NameCell } from "~/components/FirewatchRow/NameCell";
import { SpaceCell } from "~/components/FirewatchRow/SpaceCell";
import { TypeCell } from "~/components/FirewatchRow/Cells";
import { ImpactCell, RiskFactorsCell } from "~/components/Cells";
import { PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { ActionCell } from "~/components/FirewatchRow/ActionCell";

type SearchResultRowProps = {
  tableHeaders: Header[];
  pageType: AggregateScoreType;
  row: any;
};
export function SearchResultRow({
  pageType,
  row,
  tableHeaders,
}: SearchResultRowProps) {
  const shouldLinkTypes = [
    AggregateScoreType.Policy,
    AggregateScoreType.Vulnerability,
    AggregateScoreType.Advisory,
    AggregateScoreType.Asset,
    "NewestRelevantVulnerabilities",
    AggregateScoreType.Check,
    // TODO: Need to find a way to get data about the framework the control belongs to
    // and/or change the ComplianceControl page somehow to not need framework info in the URL
    // AggregateScoreType.Control,
    AggregateScoreType.Framework,
    AggregateScoreType.Software,
    "ALL",
  ];

  const shouldLink = shouldLinkTypes.includes(pageType);

  return (
    <TableRow
      {...(shouldLink &&
        row?.findingMrn && {
          component: RouterLink,
          to: getSearchLink(
            row?.scoreType,
            row.spaceId,
            row?.findingMrn,
            row?.entity?.id,
          ),
        })}
    >
      {tableHeaders.map((header) => {
        if (header.id === "IMPACT") {
          return (
            <ImpactCell
              key={`${header.id}-${row?.id}`}
              impact={row?.riskValue || 0}
              rating={row?.rating || ScoreRating.Unscored}
              isActive
            />
          );
        }
        if (header.id === "BLAST_RADIUS") {
          return (
            <BlastRadiusCell
              key={`${header.id}-${row?.id}`}
              blastRadius={row?.blastRadius}
              isActive
              rating={row?.rating || ScoreRating.Unscored}
            />
          );
        }
        if (header.id === "CHECK") {
          return <CheckCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "ADVISORY") {
          return <AdvisoryCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "VULNERABILITY") {
          return (
            <VulnerabilityCell key={`${header.id}-${row?.id}`} row={row} />
          );
        }
        if (header.id === "ASSET") {
          return (
            <AssetCell
              key={`${header.id}-${row?.id}`}
              {...{
                title: row.title || row.nodeTitle,
                tags: row.tags,
                iconId: row.iconId,
              }}
            />
          );
        }
        if (header.id === "POLICY") {
          return <PolicyNameCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "RISK_FACTORS") {
          return (
            <RiskFactorsCell
              key={`${header.id}-${row?.id}`}
              riskFactors={row?.riskFactors}
            />
          );
        }
        if (header.id === "RISK_VALUE") {
          return (
            <RiskValueCell
              key={`${header.id}-${row?.id}`}
              riskValue={row?.riskValue || 0}
            />
          );
        }
        if (header.id === "SPACE") {
          return <SpaceCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "TYPE") {
          return (
            <TypeCell key={`${header.id}-${row?.id}`} type={row.scoreType} />
          );
        }
        if (header.id === "DATE") {
          return (
            <DateCell
              key={`${header.id}-${row?.id}`}
              date={row?.firstDetectedAt}
            />
          );
        }
        if (header.id === "TITLE") {
          return <NameCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "LAST_UPDATED") {
          // TODO: Add update to mesh with first found cell
          return (
            <FirstFoundCell
              key={`${header.id}-${row?.id}`}
              discoveryTime={row?.lastScannedAt}
            />
          );
        }
        if (header.id === "CONTROL") {
          return <NameCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "SOFTWARE") {
          return <NameCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "FRAMEWORK") {
          return <NameCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        if (header.id === "ACTION") {
          return <ActionCell key={`${header.id}-${row?.id}`} row={row} />;
        }
        return <div></div>;
      })}
    </TableRow>
  );
}

export const getSearchLink = (
  type: AdjustedAggregateScoreType,
  spaceId: Space["id"],
  findingMrn: string,
  entityId: string,
) => {
  const id = findingMrn.split("/").pop();

  switch (type) {
    case AggregateScoreType.Asset:
      // TODO: this will not work, we need the asset id for this to work
      return `/space/inventory/${entityId}/overview?spaceId=${spaceId}`;
    case AggregateScoreType.Vulnerability:
    case "NewestRelevantVulnerabilities":
      return `/space/vulns/cve/${id}?spaceId=${spaceId}`;
    case AggregateScoreType.Advisory:
      return `/space/vulns/advisory/${id}?spaceId=${spaceId}`;
    case AggregateScoreType.Policy:
      return `/space/findings/policies/${PolicyMrnToURIEncodedId(findingMrn)}?spaceId=${spaceId}`;
    case AggregateScoreType.Check:
      return `/space/findings/check?spaceId=${spaceId}&findingMrn=${findingMrn}`;
    // TODO: Need to find a way to get data about the framework the control belongs to
    // and/or change the ComplianceControl page somehow to not need framework info in the URL
    // case AggregateScoreType.Control:
    //   return `/space/compliance/${frameworkId}/controls/${id}/?spaceId=${spaceId}&frameworkMrn=${frameworkMrn}8&controlMrn=${findingMrn}`;
    case AggregateScoreType.Framework:
      return `/space/compliance/${id}?spaceId=${spaceId}&frameworkMrn=${findingMrn}`;
    case AggregateScoreType.Software:
      return `/space/inventory/software/${id}?spaceId=${spaceId}&genericFindingMrn=${findingMrn}`;
    default:
      return "";
  }
};
